/**
 * Production Calculator Service for OilWellX
 * Enterprise-grade oil field production calculation engine
 * 
 * @version 3.0.0
 * @description Accurate daily production calculations using tank gauge differentials,
 *              Kolor Kut water detection, and haul ticket integration
 * 
 * KEY FORMULAS:
 * - Total Production = (Today's Level - Previous Level) + Oil Hauled
 * - Average Daily Production = Total Production / Days Between Entries
 * - Net Oil = Gross Oil - Water (from Kolor Kut readings)
 * 
 * IMPORTANT DISTINCTIONS:
 * - Oil Hauling = REVENUE (buyer pays you per barrel)
 * - Water Hauling = EXPENSE (~$300/load disposal cost)
 * - Only OIL hauled affects production calculation
 * - Water hauled is tracked but NOT added to water production
 */

import {
  TankConfiguration,
  TankReading,
  KolorKutReading,
  ProductionCalculationInput,
  TankReadingSet,
  HaulTicketSummary,
  DailyProductionResult,
  TankCalculation,
  ProductionComparison,
  ProductionAverages,
  ProductionTotals,
  VarianceAnalysis,
  ProductionAlert,
  ProductionCalculationConfig,
  TankSetupConfig,
  BatchCalculationRequest,
  BatchCalculationResult,
  StrapTableEntry,
} from '../../types/production';

import { daysBetweenIsoDates } from '../dateUtils';

// ==================== STANDARD TANK CONFIGURATIONS ====================

/**
 * Standard oil field tank sizes with pre-calculated BBL/inch values
 * Based on industry standards for upright cylindrical tanks
 * 
 * Formula: BBL/inch = (π × r² × 1) / 42
 * Where r is radius in feet, 1 inch height, 42 gal/BBL
 */
export const STANDARD_TANK_CONFIGS: Record<number, TankConfiguration> = {
  210: {
    capacityBBL: 210,
    diameterFt: 10,
    heightFt: 15,
    bblPerInch: 1.167,
  },
  300: {
    capacityBBL: 300,
    diameterFt: 12,
    heightFt: 15,
    bblPerInch: 1.680,
  },
  400: {
    capacityBBL: 400,
    diameterFt: 12,
    heightFt: 20,
    bblPerInch: 1.680,
  },
  500: {
    capacityBBL: 500,
    diameterFt: 15.5,
    heightFt: 15,
    bblPerInch: 2.804,
  },
  750: {
    capacityBBL: 750,
    diameterFt: 15.5,
    heightFt: 22,
    bblPerInch: 2.804,
  },
  1000: {
    capacityBBL: 1000,
    diameterFt: 15.5,
    heightFt: 30,
    bblPerInch: 2.804,
  },
  1500: {
    capacityBBL: 1500,
    diameterFt: 24,
    heightFt: 20,
    bblPerInch: 6.720,
  },
};

/**
 * Get BBL per inch for a given tank size
 * Falls back to calculation if size not in standard configs
 */
export function getBblPerInch(tankSizeBBL: number, diameterFt?: number): number {
  // Check standard configs first
  if (STANDARD_TANK_CONFIGS[tankSizeBBL]) {
    return STANDARD_TANK_CONFIGS[tankSizeBBL].bblPerInch;
  }
  
  // Calculate from diameter if provided
  if (diameterFt) {
    const radiusFt = diameterFt / 2;
    const squareFeet = Math.PI * radiusFt * radiusFt;
    const cubicFeetPerInch = squareFeet / 12; // 1 inch = 1/12 foot
    const gallonsPerInch = cubicFeetPerInch * 7.48052; // gallons per cubic foot
    return gallonsPerInch / 42; // 42 gallons per barrel
  }
  
  // Estimate based on typical tank proportions
  // Assuming standard height-to-diameter ratio
  const estimatedDiameter = Math.sqrt(tankSizeBBL / 3.14159 / 0.5) * 2;
  return getBblPerInch(tankSizeBBL, estimatedDiameter);
}

/**
 * Convert tank gauge reading (inches) to barrels
 */
export function inchesToBarrels(
  inches: number,
  tankSizeBBL: number,
  customBblPerInch?: number,
  strapTable?: StrapTableEntry[]
): number {
  // Use strap table if available (most accurate)
  if (strapTable && strapTable.length > 0) {
    return interpolateStrapTable(inches, strapTable);
  }
  
  // Use custom BBL/inch if provided
  const bblPerInch = customBblPerInch || getBblPerInch(tankSizeBBL);
  return Math.round((inches * bblPerInch) * 100) / 100; // Round to 2 decimals
}

/**
 * Interpolate strap table for precise volume calculation
 */
function interpolateStrapTable(inches: number, strapTable: StrapTableEntry[]): number {
  // Sort by inches ascending
  const sorted = [...strapTable].sort((a, b) => a.inches - b.inches);
  
  // Find bounding entries
  const lower = sorted.filter(e => e.inches <= inches).pop();
  const upper = sorted.find(e => e.inches >= inches);
  
  // Exact match
  if (lower && lower.inches === inches) return lower.barrels;
  if (upper && upper.inches === inches) return upper.barrels;
  
  // Interpolate between points
  if (lower && upper) {
    const ratio = (inches - lower.inches) / (upper.inches - lower.inches);
    return lower.barrels + (upper.barrels - lower.barrels) * ratio;
  }
  
  // Below or above table range - extrapolate
  if (!lower && upper) {
    const nextUpper = sorted.find(e => e.inches > upper.inches);
    if (nextUpper) {
      const bblPerInch = (nextUpper.barrels - upper.barrels) / (nextUpper.inches - upper.inches);
      return upper.barrels - bblPerInch * (upper.inches - inches);
    }
    return 0;
  }
  
  if (lower && !upper) {
    const prevLower = sorted.filter(e => e.inches < lower.inches).pop();
    if (prevLower) {
      const bblPerInch = (lower.barrels - prevLower.barrels) / (lower.inches - prevLower.inches);
      return lower.barrels + bblPerInch * (inches - lower.inches);
    }
    return lower.barrels;
  }
  
  return 0;
}

// ==================== KOLOR KUT CALCULATIONS ====================

/**
 * Calculate net oil and water from Kolor Kut reading
 * Kolor Kut paste turns red/pink when contacting water, showing water level
 */
export function calculateKolorKutAdjustment(
  totalFluidBBL: number,
  _gaugeInches: number, // Reserved for future strap table calculations
  kolorKut: KolorKutReading,
  tankSizeBBL: number,
  customBblPerInch?: number
): { netOilBBL: number; waterBBL: number; waterCutPercent: number } {
  if (!kolorKut.enabled || kolorKut.waterCutInches <= 0) {
    return {
      netOilBBL: totalFluidBBL,
      waterBBL: 0,
      waterCutPercent: 0,
    };
  }
  
  const bblPerInch = customBblPerInch || getBblPerInch(tankSizeBBL);
  const waterBBL = kolorKut.waterCutInches * bblPerInch;
  const netOilBBL = Math.max(0, totalFluidBBL - waterBBL);
  const waterCutPercent = totalFluidBBL > 0 
    ? (waterBBL / totalFluidBBL) * 100 
    : 0;
  
  return {
    netOilBBL: Math.round(netOilBBL * 100) / 100,
    waterBBL: Math.round(waterBBL * 100) / 100,
    waterCutPercent: Math.round(waterCutPercent * 10) / 10,
  };
}

// ==================== DAILY PRODUCTION CALCULATION ====================

/**
 * Calculate individual tank's daily production
 */
export function calculateTankProduction(
  tankNumber: number,
  type: 'oil' | 'water',
  todayReading: TankReading | undefined,
  yesterdayReading: TankReading | undefined,
  barrelsHauled: number,
  config?: TankSetupConfig
): TankCalculation {
  // Handle missing readings
  if (!todayReading && !yesterdayReading) {
    return createEmptyTankCalculation(tankNumber, type, barrelsHauled);
  }
  
  const tankSizeBBL = todayReading?.tankSizeBBL || yesterdayReading?.tankSizeBBL || 500;
  const bblPerInch = config?.customBblPerInch || getBblPerInch(tankSizeBBL);
  
  // Get gauge readings
  const todayGaugeInches = todayReading?.gaugeInches || 0;
  const yesterdayGaugeInches = yesterdayReading?.gaugeInches || 0;
  
  // Calculate volumes
  const todayVolumeBBL = inchesToBarrels(todayGaugeInches, tankSizeBBL, bblPerInch);
  const yesterdayVolumeBBL = inchesToBarrels(yesterdayGaugeInches, tankSizeBBL, bblPerInch);
  
  // Level change (can be negative if level dropped)
  const levelChangeBBL = todayVolumeBBL - yesterdayVolumeBBL;
  
  // Daily production = level change + what was hauled
  // If level dropped 50 BBL and we hauled 100 BBL, we produced 50 BBL
  // If level rose 20 BBL and we hauled 0 BBL, we produced 20 BBL
  const dailyProductionBBL = levelChangeBBL + barrelsHauled;
  
  // Kolor Kut adjustment (oil tanks only)
  let kolorKutWaterInches: number | undefined;
  let kolorKutWaterBBL: number | undefined;
  let netProductionBBL = dailyProductionBBL;
  
  if (type === 'oil' && todayReading?.kolorKut?.enabled) {
    kolorKutWaterInches = todayReading.kolorKut.waterCutInches;
    kolorKutWaterBBL = kolorKutWaterInches * bblPerInch;
    
    // Net production removes water detected by Kolor Kut
    // But only if we're measuring today's tank - the water detected is in today's reading
    // So we subtract today's water from today's net calculation
    const todayNetOil = todayVolumeBBL - kolorKutWaterBBL;
    const yesterdayNetOil = yesterdayReading?.kolorKut?.enabled 
      ? yesterdayVolumeBBL - (yesterdayReading.kolorKut.waterCutInches * bblPerInch)
      : yesterdayVolumeBBL;
    
    netProductionBBL = (todayNetOil - yesterdayNetOil) + barrelsHauled;
  }
  
  return {
    tankNumber,
    type,
    tankSizeBBL,
    bblPerInch,
    yesterdayGaugeInches,
    yesterdayVolumeBBL,
    todayGaugeInches,
    todayVolumeBBL,
    kolorKutWaterInches,
    kolorKutWaterBBL,
    barrelsHauled,
    levelChangeBBL: Math.round(levelChangeBBL * 100) / 100,
    dailyProductionBBL: Math.round(dailyProductionBBL * 100) / 100,
    netProductionBBL: Math.round(netProductionBBL * 100) / 100,
  };
}

function createEmptyTankCalculation(
  tankNumber: number, 
  type: 'oil' | 'water',
  barrelsHauled: number
): TankCalculation {
  return {
    tankNumber,
    type,
    tankSizeBBL: 0,
    bblPerInch: 0,
    yesterdayGaugeInches: 0,
    yesterdayVolumeBBL: 0,
    todayGaugeInches: 0,
    todayVolumeBBL: 0,
    barrelsHauled,
    levelChangeBBL: 0,
    dailyProductionBBL: barrelsHauled, // Only count what was hauled
    netProductionBBL: barrelsHauled,
  };
}

/**
 * Main calculation function - Calculate daily production for a well
 */
export function calculateDailyProduction(
  input: ProductionCalculationInput,
  config?: ProductionCalculationConfig
): DailyProductionResult {
  const warnings: string[] = [];
  let confidenceScore = 100;
  
  // Validate input
  if (!input.todayReadings || !input.yesterdayReadings) {
    warnings.push('Missing readings - using available data only');
    confidenceScore -= 30;
  }
  
  // Calculate oil tank productions
  const oilTankCalculations: TankCalculation[] = [];
  const oilTankNumbers = new Set([
    ...input.todayReadings.oilTanks.map(t => t.tankNumber),
    ...input.yesterdayReadings.oilTanks.map(t => t.tankNumber),
  ]);
  
  for (const tankNum of oilTankNumbers) {
    const todayTank = input.todayReadings.oilTanks.find(t => t.tankNumber === tankNum);
    const yesterdayTank = input.yesterdayReadings.oilTanks.find(t => t.tankNumber === tankNum);
    
    // Find haul tickets for this tank
    const tankHauls = input.haulTickets.filter(
      h => h.type === 'oil' && h.tankNumbers.includes(tankNum)
    );
    const barrelsHauled = tankHauls.reduce((sum, h) => sum + (h.netBarrels || h.barrels), 0);
    
    const tankConfig = config?.oilTanks.find(t => t.tankNumber === tankNum);
    const calc = calculateTankProduction(tankNum, 'oil', todayTank, yesterdayTank, barrelsHauled, tankConfig);
    oilTankCalculations.push(calc);
    
    // Check for warnings
    if (!todayTank && yesterdayTank) {
      warnings.push(`Oil Tank ${tankNum}: Missing today's reading`);
      confidenceScore -= 10;
    }
    if (calc.dailyProductionBBL < 0) {
      warnings.push(`Oil Tank ${tankNum}: Negative production (${calc.dailyProductionBBL} BBL) - check readings`);
      confidenceScore -= 5;
    }
  }
  
  // Calculate water tank productions
  const waterTankCalculations: TankCalculation[] = [];
  const waterTankNumbers = new Set([
    ...input.todayReadings.waterTanks.map(t => t.tankNumber),
    ...input.yesterdayReadings.waterTanks.map(t => t.tankNumber),
  ]);
  
  for (const tankNum of waterTankNumbers) {
    const todayTank = input.todayReadings.waterTanks.find(t => t.tankNumber === tankNum);
    const yesterdayTank = input.yesterdayReadings.waterTanks.find(t => t.tankNumber === tankNum);
    
    const tankHauls = input.haulTickets.filter(
      h => h.type === 'water' && h.tankNumbers.includes(tankNum)
    );
    const barrelsHauled = tankHauls.reduce((sum, h) => sum + h.barrels, 0);
    
    const tankConfig = config?.waterTanks.find(t => t.tankNumber === tankNum);
    const calc = calculateTankProduction(tankNum, 'water', todayTank, yesterdayTank, barrelsHauled, tankConfig);
    waterTankCalculations.push(calc);
  }
  
  // Aggregate oil production
  const grossOilBBL = oilTankCalculations.reduce((sum, t) => sum + t.dailyProductionBBL, 0);
  const waterInOilTanksBBL = oilTankCalculations.reduce((sum, t) => sum + (t.kolorKutWaterBBL || 0), 0);
  const netOilBBL = oilTankCalculations.reduce((sum, t) => sum + t.netProductionBBL, 0);
  const oilHauledBBL = input.haulTickets
    .filter(h => h.type === 'oil')
    .reduce((sum, h) => sum + (h.netBarrels || h.barrels), 0);
  const oilInTanksBBL = oilTankCalculations.reduce((sum, t) => sum + t.todayVolumeBBL, 0);
  
  // Aggregate water production
  const grossWaterBBL = waterTankCalculations.reduce((sum, t) => sum + t.dailyProductionBBL, 0);
  const waterHauledBBL = input.haulTickets
    .filter(h => h.type === 'water')
    .reduce((sum, h) => sum + h.barrels, 0);
  const waterInTanksBBL = waterTankCalculations.reduce((sum, t) => sum + t.todayVolumeBBL, 0);
  
  // Gas production
  const gasMCF = input.gasMeter?.production || 
    (input.gasMeter?.currentReading && input.gasMeter?.previousReading 
      ? (input.gasMeter.currentReading - input.gasMeter.previousReading) * (input.gasMeter.multiplier || 1)
      : 0);
  
  // Calculated metrics
  const totalFluidBBL = netOilBBL + grossWaterBBL + waterInOilTanksBBL;
  const waterCutPercent = totalFluidBBL > 0 
    ? ((grossWaterBBL + waterInOilTanksBBL) / totalFluidBBL) * 100 
    : 0;
  const gasOilRatio = netOilBBL > 0 ? gasMCF / netOilBBL : 0;
  
  // Financial calculations
  const oilPricePerBBL = config?.oilPricePerBBL || 75; // Default price
  const waterHaulCostPerBBL = config?.waterHaulCostPerBBL || 2.50;
  const waterDisposalCostPerBBL = config?.waterDisposalCostPerBBL || 1.50;
  
  const oilRevenue = netOilBBL * oilPricePerBBL;
  const waterDisposalCost = waterHauledBBL * (waterHaulCostPerBBL + waterDisposalCostPerBBL);
  const netRevenue = oilRevenue - waterDisposalCost;
  const costPerBBL = netOilBBL > 0 ? waterDisposalCost / netOilBBL : 0;
  
  // Collect all tank calculations
  const tankCalculations = [...oilTankCalculations, ...waterTankCalculations];
  const haulTicketIds = input.haulTickets.map(h => h.id);
  
  // Confidence adjustments
  if (haulTicketIds.length === 0 && oilTankCalculations.some(t => t.levelChangeBBL < -10)) {
    warnings.push('Tank level dropped significantly but no haul tickets found');
    confidenceScore -= 15;
  }
  
  return {
    wellId: input.wellId,
    wellName: input.wellName,
    date: input.todayDate,
    
    // Oil
    grossOilBBL: Math.round(grossOilBBL * 100) / 100,
    waterInOilTanksBBL: Math.round(waterInOilTanksBBL * 100) / 100,
    netOilBBL: Math.round(netOilBBL * 100) / 100,
    oilHauledBBL: Math.round(oilHauledBBL * 100) / 100,
    oilInTanksBBL: Math.round(oilInTanksBBL * 100) / 100,
    
    // Water
    grossWaterBBL: Math.round(grossWaterBBL * 100) / 100,
    waterHauledBBL: Math.round(waterHauledBBL * 100) / 100,
    waterInTanksBBL: Math.round(waterInTanksBBL * 100) / 100,
    
    // Gas
    gasMCF: Math.round(gasMCF * 100) / 100,
    
    // Metrics
    waterCutPercent: Math.round(waterCutPercent * 10) / 10,
    gasOilRatio: Math.round(gasOilRatio * 100) / 100,
    totalFluidBBL: Math.round(totalFluidBBL * 100) / 100,
    
    // Financial
    oilRevenue: Math.round(oilRevenue * 100) / 100,
    waterDisposalCost: Math.round(waterDisposalCost * 100) / 100,
    netRevenue: Math.round(netRevenue * 100) / 100,
    costPerBBL: Math.round(costPerBBL * 100) / 100,
    
    // Details
    tankCalculations,
    haulTicketIds,
    
    // Metadata
    calculatedAt: new Date(),
    calculationVersion: '2.0.0',
    confidenceScore: Math.max(0, confidenceScore),
    warnings,
  };
}

// ==================== HISTORICAL COMPARISON ====================

/**
 * Calculate rolling averages for a period
 */
export function calculateAverages(
  results: DailyProductionResult[],
  startDate: string,
  endDate: string
): ProductionAverages {
  if (results.length === 0) {
    return {
      startDate,
      endDate,
      daysCount: 0,
      avgOilBBL: 0,
      avgWaterBBL: 0,
      avgGasMCF: 0,
      avgWaterCutPercent: 0,
      avgGasOilRatio: 0,
    };
  }
  
  const totalOil = results.reduce((sum, r) => sum + r.netOilBBL, 0);
  const totalWater = results.reduce((sum, r) => sum + r.grossWaterBBL, 0);
  const totalGas = results.reduce((sum, r) => sum + r.gasMCF, 0);
  const totalWaterCut = results.reduce((sum, r) => sum + r.waterCutPercent, 0);
  const totalGOR = results.reduce((sum, r) => sum + r.gasOilRatio, 0);
  
  const count = results.length;
  
  return {
    startDate,
    endDate,
    daysCount: count,
    avgOilBBL: Math.round((totalOil / count) * 100) / 100,
    avgWaterBBL: Math.round((totalWater / count) * 100) / 100,
    avgGasMCF: Math.round((totalGas / count) * 100) / 100,
    avgWaterCutPercent: Math.round((totalWaterCut / count) * 10) / 10,
    avgGasOilRatio: Math.round((totalGOR / count) * 100) / 100,
  };
}

/**
 * Calculate period totals
 */
export function calculateTotals(
  results: DailyProductionResult[],
  startDate: string,
  endDate: string
): ProductionTotals {
  const producingResults = results.filter(r => r.netOilBBL > 0 || r.grossWaterBBL > 0 || r.gasMCF > 0);
  
  return {
    startDate,
    endDate,
    producingDays: producingResults.length,
    downDays: results.length - producingResults.length,
    totalOilBBL: Math.round(results.reduce((sum, r) => sum + r.netOilBBL, 0) * 100) / 100,
    totalWaterBBL: Math.round(results.reduce((sum, r) => sum + r.grossWaterBBL, 0) * 100) / 100,
    totalGasMCF: Math.round(results.reduce((sum, r) => sum + r.gasMCF, 0) * 100) / 100,
    totalOilRevenue: Math.round(results.reduce((sum, r) => sum + (r.oilRevenue || 0), 0) * 100) / 100,
    totalWaterCost: Math.round(results.reduce((sum, r) => sum + (r.waterDisposalCost || 0), 0) * 100) / 100,
    totalNetRevenue: Math.round(results.reduce((sum, r) => sum + (r.netRevenue || 0), 0) * 100) / 100,
  };
}

/**
 * Analyze variance between current and historical production
 */
export function analyzeVariance(
  today: DailyProductionResult,
  yesterday?: DailyProductionResult,
  sevenDayAvg?: ProductionAverages,
  thirtyDayAvg?: ProductionAverages,
  config?: ProductionCalculationConfig
): VarianceAnalysis {
  const alerts: ProductionAlert[] = [];
  
  // Calculate variances
  const oilVsYesterdayPercent = yesterday && yesterday.netOilBBL > 0
    ? ((today.netOilBBL - yesterday.netOilBBL) / yesterday.netOilBBL) * 100
    : undefined;
    
  const oilVsSevenDayPercent = sevenDayAvg && sevenDayAvg.avgOilBBL > 0
    ? ((today.netOilBBL - sevenDayAvg.avgOilBBL) / sevenDayAvg.avgOilBBL) * 100
    : undefined;
    
  const oilVsThirtyDayPercent = thirtyDayAvg && thirtyDayAvg.avgOilBBL > 0
    ? ((today.netOilBBL - thirtyDayAvg.avgOilBBL) / thirtyDayAvg.avgOilBBL) * 100
    : undefined;
    
  const waterCutVsSevenDayPercent = sevenDayAvg && sevenDayAvg.avgWaterCutPercent > 0
    ? ((today.waterCutPercent - sevenDayAvg.avgWaterCutPercent) / sevenDayAvg.avgWaterCutPercent) * 100
    : undefined;
  
  // Determine trend
  let oilTrend: 'increasing' | 'stable' | 'declining' = 'stable';
  if (oilVsSevenDayPercent !== undefined) {
    if (oilVsSevenDayPercent > 10) oilTrend = 'increasing';
    else if (oilVsSevenDayPercent < -10) oilTrend = 'declining';
  }
  
  // Generate alerts
  const thresholds = config?.thresholds || {
    maxWaterCutPercent: 50,
    minDailyOilBBL: 5,
    maxDailyOilBBL: 200,
    oilDropWarningPercent: 20,
    tankFullWarningPercent: 90,
    tankEmptyWarningPercent: 10,
  };
  
  // Oil drop alert
  if (oilVsSevenDayPercent !== undefined && oilVsSevenDayPercent < -thresholds.oilDropWarningPercent) {
    alerts.push({
      type: 'oil_drop',
      severity: oilVsSevenDayPercent < -40 ? 'critical' : 'warning',
      message: `Oil production down ${Math.abs(Math.round(oilVsSevenDayPercent))}% vs 7-day average`,
      threshold: -thresholds.oilDropWarningPercent,
      actualValue: oilVsSevenDayPercent,
      recommendation: 'Check pump operation, wellbore issues, or if well is shutting down',
      generatedAt: new Date(),
    });
  }
  
  // Oil spike alert
  if (oilVsSevenDayPercent !== undefined && oilVsSevenDayPercent > 50) {
    alerts.push({
      type: 'oil_spike',
      severity: 'info',
      message: `Oil production up ${Math.round(oilVsSevenDayPercent)}% vs 7-day average`,
      threshold: 50,
      actualValue: oilVsSevenDayPercent,
      recommendation: 'Verify readings - could indicate data entry error or legitimate increase',
      generatedAt: new Date(),
    });
  }
  
  // Water cut alerts
  if (today.waterCutPercent > thresholds.maxWaterCutPercent) {
    alerts.push({
      type: 'water_cut_high',
      severity: today.waterCutPercent > 70 ? 'critical' : 'warning',
      message: `Water cut at ${Math.round(today.waterCutPercent)}% exceeds threshold`,
      threshold: thresholds.maxWaterCutPercent,
      actualValue: today.waterCutPercent,
      recommendation: 'Consider chemical treatment or well intervention',
      generatedAt: new Date(),
    });
  }
  
  if (waterCutVsSevenDayPercent !== undefined && waterCutVsSevenDayPercent > 20) {
    alerts.push({
      type: 'water_cut_rising',
      severity: 'warning',
      message: `Water cut rising - up ${Math.round(waterCutVsSevenDayPercent)}% vs 7-day average`,
      threshold: 20,
      actualValue: waterCutVsSevenDayPercent,
      recommendation: 'Monitor closely for water breakthrough',
      generatedAt: new Date(),
    });
  }
  
  // Tank level alerts
  for (const tank of today.tankCalculations) {
    if (tank.type === 'oil') {
      const tankConfig = STANDARD_TANK_CONFIGS[tank.tankSizeBBL];
      if (tankConfig) {
        const fillPercent = (tank.todayVolumeBBL / tank.tankSizeBBL) * 100;
        
        if (fillPercent > thresholds.tankFullWarningPercent) {
          alerts.push({
            type: 'tank_near_full',
            severity: fillPercent > 95 ? 'critical' : 'warning',
            message: `Oil Tank ${tank.tankNumber} at ${Math.round(fillPercent)}% capacity`,
            threshold: thresholds.tankFullWarningPercent,
            actualValue: fillPercent,
            recommendation: 'Schedule oil haul immediately',
            generatedAt: new Date(),
          });
        }
      }
    }
    
    if (tank.type === 'water') {
      const fillPercent = (tank.todayVolumeBBL / tank.tankSizeBBL) * 100;
      if (fillPercent > thresholds.tankFullWarningPercent) {
        alerts.push({
          type: 'tank_near_full',
          severity: fillPercent > 95 ? 'critical' : 'warning',
          message: `Water Tank ${tank.tankNumber} at ${Math.round(fillPercent)}% capacity`,
          threshold: thresholds.tankFullWarningPercent,
          actualValue: fillPercent,
          recommendation: 'Schedule water disposal immediately',
          generatedAt: new Date(),
        });
      }
    }
  }
  
  // Data quality alerts
  if (today.confidenceScore < 70) {
    alerts.push({
      type: 'data_missing',
      severity: 'warning',
      message: `Low data confidence (${today.confidenceScore}%) - ${today.warnings.join(', ')}`,
      actualValue: today.confidenceScore,
      recommendation: 'Review and complete missing data entries',
      generatedAt: new Date(),
    });
  }
  
  // Determine overall alert level
  let alertLevel: 'normal' | 'watch' | 'warning' | 'critical' = 'normal';
  if (alerts.some(a => a.severity === 'critical')) alertLevel = 'critical';
  else if (alerts.some(a => a.severity === 'warning')) alertLevel = 'warning';
  else if (alerts.some(a => a.severity === 'info')) alertLevel = 'watch';
  
  return {
    oilVsYesterdayPercent: oilVsYesterdayPercent !== undefined 
      ? Math.round(oilVsYesterdayPercent * 10) / 10 
      : undefined,
    oilVsSevenDayPercent: oilVsSevenDayPercent !== undefined 
      ? Math.round(oilVsSevenDayPercent * 10) / 10 
      : undefined,
    oilVsThirtyDayPercent: oilVsThirtyDayPercent !== undefined 
      ? Math.round(oilVsThirtyDayPercent * 10) / 10 
      : undefined,
    waterCutVsSevenDayPercent: waterCutVsSevenDayPercent !== undefined 
      ? Math.round(waterCutVsSevenDayPercent * 10) / 10 
      : undefined,
    oilTrend,
    trendDays: 1, // Would need historical data to calculate properly
    alertLevel,
    alerts,
  };
}

/**
 * Build complete production comparison with historical data
 */
export function buildProductionComparison(
  today: DailyProductionResult,
  historicalResults: DailyProductionResult[],
  config?: ProductionCalculationConfig
): ProductionComparison {
  // Sort by date descending
  const sorted = [...historicalResults].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  
  // Get yesterday's result
  const yesterday = sorted.find(r => r.date !== today.date);
  
  // Get last 7 days (excluding today)
  const last7Days = sorted.filter(r => r.date !== today.date).slice(0, 7);
  const sevenDayAverage = last7Days.length > 0 
    ? calculateAverages(
        last7Days,
        last7Days[last7Days.length - 1].date,
        last7Days[0].date
      )
    : undefined;
  
  // Get last 30 days
  const last30Days = sorted.filter(r => r.date !== today.date).slice(0, 30);
  const thirtyDayAverage = last30Days.length > 0
    ? calculateAverages(
        last30Days,
        last30Days[last30Days.length - 1].date,
        last30Days[0].date
      )
    : undefined;
  
  // Month-to-date (all results in same month as today)
  const todayMonth = today.date.substring(0, 7); // YYYY-MM
  const mtdResults = sorted.filter(r => r.date.startsWith(todayMonth));
  const monthToDate = mtdResults.length > 0
    ? calculateTotals(
        mtdResults,
        `${todayMonth}-01`,
        today.date
      )
    : undefined;
  
  // Year-to-date
  const todayYear = today.date.substring(0, 4);
  const ytdResults = sorted.filter(r => r.date.startsWith(todayYear));
  const yearToDate = ytdResults.length > 0
    ? calculateTotals(
        ytdResults,
        `${todayYear}-01-01`,
        today.date
      )
    : undefined;
  
  // Variance analysis
  const variance = analyzeVariance(today, yesterday, sevenDayAverage, thirtyDayAverage, config);
  
  return {
    today,
    yesterday,
    sevenDayAverage,
    thirtyDayAverage,
    monthToDate,
    yearToDate,
    variance,
  };
}

// ==================== BATCH OPERATIONS ====================

/**
 * Calculate production for multiple wells (batch operation)
 * Used for daily reports and dashboard aggregations
 */
export async function calculateBatchProduction(
  request: BatchCalculationRequest,
  productionData: Map<string, { today: TankReadingSet; yesterday: TankReadingSet }>,
  haulTickets: HaulTicketSummary[],
  wellConfigs: Map<string, ProductionCalculationConfig>
): Promise<BatchCalculationResult> {
  const startTime = Date.now();
  const wellResults: DailyProductionResult[] = [];
  const allAlerts: Array<ProductionAlert & { wellId: string; wellName: string }> = [];
  
  for (const [wellId, data] of productionData) {
    const config = wellConfigs.get(wellId);
    const wellHaulTickets = haulTickets.filter(ticket => ticket.wellId === wellId);
    
    const input: ProductionCalculationInput = {
      wellId,
      wellName: config?.wellId || wellId, // Would need well name from config
      todayDate: request.date,
      yesterdayDate: getPreviousDate(request.date),
      todayReadings: data.today,
      yesterdayReadings: data.yesterday,
      haulTickets: wellHaulTickets,
    };
    
    const result = calculateDailyProduction(input, config);
    wellResults.push(result);
    
    // Collect alerts with well info
    const comparison = buildProductionComparison(result, [], config);
    for (const alert of comparison.variance.alerts) {
      allAlerts.push({
        ...alert,
        wellId,
        wellName: result.wellName,
      });
    }
  }
  
  // Calculate totals
  const totals = {
    totalNetOilBBL: wellResults.reduce((sum, r) => sum + r.netOilBBL, 0),
    totalGrossWaterBBL: wellResults.reduce((sum, r) => sum + r.grossWaterBBL, 0),
    totalGasMCF: wellResults.reduce((sum, r) => sum + r.gasMCF, 0),
    avgWaterCutPercent: wellResults.length > 0
      ? wellResults.reduce((sum, r) => sum + r.waterCutPercent, 0) / wellResults.length
      : 0,
    totalOilRevenue: wellResults.reduce((sum, r) => sum + (r.oilRevenue || 0), 0),
    totalWaterCost: wellResults.reduce((sum, r) => sum + (r.waterDisposalCost || 0), 0),
    totalNetRevenue: wellResults.reduce((sum, r) => sum + (r.netRevenue || 0), 0),
    wellsCalculated: wellResults.length,
    wellsWithWarnings: wellResults.filter(r => r.warnings.length > 0).length,
    wellsWithCritical: allAlerts.filter(a => a.severity === 'critical').length,
  };
  
  return {
    date: request.date,
    companyId: request.companyId,
    wellResults,
    totals: {
      ...totals,
      totalNetOilBBL: Math.round(totals.totalNetOilBBL * 100) / 100,
      totalGrossWaterBBL: Math.round(totals.totalGrossWaterBBL * 100) / 100,
      totalGasMCF: Math.round(totals.totalGasMCF * 100) / 100,
      avgWaterCutPercent: Math.round(totals.avgWaterCutPercent * 10) / 10,
      totalOilRevenue: Math.round(totals.totalOilRevenue * 100) / 100,
      totalWaterCost: Math.round(totals.totalWaterCost * 100) / 100,
      totalNetRevenue: Math.round(totals.totalNetRevenue * 100) / 100,
    },
    allAlerts,
    calculatedAt: new Date(),
    processingTimeMs: Date.now() - startTime,
  };
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Get previous date string (YYYY-MM-DD)
 */
export function getPreviousDate(dateStr: string): string {
  const date = new Date(dateStr);
  date.setDate(date.getDate() - 1);
  return date.toISOString().split('T')[0];
}

/**
 * Get date range for comparison
 */
export function getDateRange(endDate: string, days: number): { start: string; end: string } {
  const end = new Date(endDate);
  const start = new Date(endDate);
  start.setDate(start.getDate() - days);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

/**
 * Format date for display
 */
export function formatProductionDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Calculate tank fill percentage
 */
export function getTankFillPercent(volumeBBL: number, capacityBBL: number): number {
  if (capacityBBL <= 0) return 0;
  return Math.min(100, Math.round((volumeBBL / capacityBBL) * 1000) / 10);
}

/**
 * Get tank status based on fill level
 */
export function getTankStatus(fillPercent: number): 'empty' | 'low' | 'normal' | 'high' | 'full' {
  if (fillPercent <= 5) return 'empty';
  if (fillPercent <= 20) return 'low';
  if (fillPercent <= 80) return 'normal';
  if (fillPercent <= 95) return 'high';
  return 'full';
}

/**
 * Validate production entry data
 */
export function validateProductionEntry(entry: TankReadingSet): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Check for required data
  if (!entry.entryDate) {
    errors.push('Entry date is required');
  }
  
  if (!entry.enteredBy) {
    errors.push('Entered by is required');
  }
  
  // Check tank readings
  for (const tank of entry.oilTanks) {
    if (tank.gaugeInches < 0) {
      errors.push(`Oil Tank ${tank.tankNumber}: Gauge reading cannot be negative`);
    }
    
    const maxInches = (STANDARD_TANK_CONFIGS[tank.tankSizeBBL]?.heightFt || 30) * 12;
    if (tank.gaugeInches > maxInches) {
      errors.push(`Oil Tank ${tank.tankNumber}: Gauge reading exceeds tank height`);
    }
    
    if (tank.kolorKut?.enabled && tank.kolorKut.waterCutInches > tank.gaugeInches) {
      errors.push(`Oil Tank ${tank.tankNumber}: Kolor Kut reading exceeds total fluid level`);
    }
  }
  
  for (const tank of entry.waterTanks) {
    if (tank.gaugeInches < 0) {
      errors.push(`Water Tank ${tank.tankNumber}: Gauge reading cannot be negative`);
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

// ==================== SAVE-TIME VALIDATION ====================

/**
 * Validation result for pre-save checks
 */
export interface EntryValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validate production entry before save
 * Returns errors (block save) and warnings (allow save with confirmation)
 * 
 * @param entryDate - Entry date in YYYY-MM-DD format
 * @param oilTanks - Oil tank gauges
 * @param waterTanks - Water tank gauges
 * @param calculatedProduction - Calculated values
 * @param previousEntry - Previous entry data for comparison
 */
export function validateEntryBeforeSave(
  entryDate: string,
  oilTanks: StoredTankGauge[],
  waterTanks: StoredTankGauge[],
  calculatedProduction: EntryCalculatedTotals,
  previousEntry: PreviousEntryData | null
): EntryValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // ===== ERRORS (block save) =====
  
  // Date validation
  if (!entryDate || !/^\d{4}-\d{2}-\d{2}$/.test(entryDate)) {
    errors.push('Invalid entry date format');
  }
  
  // Future date check
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const entryDateObj = new Date(entryDate);
  if (entryDateObj > today) {
    errors.push('Cannot submit entries for future dates');
  }
  
  // Ancient date check (more than 30 days old)
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  if (entryDateObj < thirtyDaysAgo) {
    errors.push('Entry date is more than 30 days old - requires supervisor approval');
  }
  
  // Negative values
  if (calculatedProduction.totalOilBBL < 0) {
    errors.push('Total oil volume cannot be negative');
  }
  if (calculatedProduction.grossWaterBBL < 0) {
    errors.push('Total water volume cannot be negative');
  }
  
  // NaN/undefined checks
  if (isNaN(calculatedProduction.totalOilBBL) || calculatedProduction.totalOilBBL === undefined) {
    errors.push('Oil volume calculation failed - check tank readings');
  }
  if (isNaN(calculatedProduction.waterCutPercent) || calculatedProduction.waterCutPercent === undefined) {
    errors.push('Water cut calculation failed');
  }
  
  // ===== WARNINGS (allow with confirmation) =====
  
  // Large daily production change (>50% swing) - adjusted for multi-day gaps
  if (previousEntry && calculatedProduction.hasPreviousEntryData) {
    const netOilChange = Math.abs(calculatedProduction.netOilBBL - previousEntry.netOilBBL);
    const avgOil = (calculatedProduction.netOilBBL + previousEntry.netOilBBL) / 2;
    // Scale threshold by days between entries (more variance expected over longer periods)
    const swingThreshold = 0.5 * Math.sqrt(calculatedProduction.daysBetweenEntries);
    if (avgOil > 10 && netOilChange > avgOil * swingThreshold) {
      warnings.push(`Large oil level change: ${netOilChange.toFixed(1)} BBL (${((netOilChange / avgOil) * 100).toFixed(0)}% swing over ${calculatedProduction.daysBetweenEntries} day${calculatedProduction.daysBetweenEntries > 1 ? 's' : ''})`);
    }
  }
  
  // Negative avg daily production (went backwards without hauling)
  if (calculatedProduction.avgDailyOilProduction < 0 && calculatedProduction.oilHauledBBL === 0) {
    warnings.push(`Negative avg daily production: ${calculatedProduction.avgDailyOilProduction.toFixed(1)} BBL/day - check if hauling was recorded`);
  }
  
  // Unusually high water cut (>50%)
  if (calculatedProduction.waterCutPercent > 50) {
    warnings.push(`High water cut: ${calculatedProduction.waterCutPercent.toFixed(1)}% - well may need attention`);
  }
  
  // Zero readings when previous had data
  if (previousEntry && calculatedProduction.totalOilBBL === 0 && previousEntry.netOilBBL > 10) {
    warnings.push('All tanks show zero oil - verify readings are correct');
  }
  
  // Kolor-Kut water exceeds reasonable percentage (>30% of oil volume)
  if (calculatedProduction.kolorKutWaterBBL > calculatedProduction.totalOilBBL * 0.3 && calculatedProduction.kolorKutWaterBBL > 5) {
    warnings.push(`High Kolor-Kut water: ${calculatedProduction.kolorKutWaterBBL.toFixed(1)} BBL (${((calculatedProduction.kolorKutWaterBBL / calculatedProduction.totalOilBBL) * 100).toFixed(0)}% of oil volume)`);
  }
  
  // No tank readings at all
  if (oilTanks.length === 0 && waterTanks.length === 0) {
    warnings.push('No tank readings entered');
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

// ==================== ENTRY TOTALS FOR SAVE-TIME CALCULATION ====================

/**
 * Simplified tank gauge data format as stored in production entries
 * Matches the tankGauges structure saved in DataEntry.tsx
 */
export interface StoredTankGauge {
  tankId: string;
  tankNumber: number;
  barrels: number; // Net volume calculated
  topGauge: number;
  bottomGauge: number;
  kolorKut?: {
    waterCutInches: number;
    notes?: string;
  };
}

/**
 * Haul ticket summary for calculations
 */
export interface HaulSummary {
  oilBBL: number;
  waterBBL: number;
}

/**
 * Result of calculateEntryTotals - saved with production entry
 * Matches WellCalculatedProduction interface in database.ts
 */
export interface EntryCalculatedTotals {
  /** Date these calculations were made (YYYY-MM-DD) */
  calculatedFor: string;
  /** Total oil from all tanks in BBL (sum of tank readings) */
  totalOilBBL: number;
  /** Net oil after Kolor-Kut water deduction */
  netOilBBL: number;
  /** Water detected via Kolor-Kut in BBL */
  kolorKutWaterBBL: number;
  /** Total water from water tanks in BBL */
  grossWaterBBL: number;
  /** Water cut percentage = water / (oil + water) * 100 */
  waterCutPercent: number;
  
  // ==================== PRODUCTION CALCULATIONS ====================
  
  /** 
   * Total oil production since last entry = (todayNet - previousNet) + oilHauled 
   * This is the TOTAL production over daysBetweenEntries days
   */
  totalOilProduction: number;
  /** 
   * Total water produced since last entry = (todayWater - previousWater)
   * NOTE: Water hauled is NOT added - water hauling is disposal (expense), not production
   */
  totalWaterProduced: number;
  /** 
   * Average daily oil production = totalOilProduction / daysBetweenEntries
   * Use this for consistent reporting regardless of check frequency
   */
  avgDailyOilProduction: number;
  /** 
   * Average daily water production = totalWaterProduced / daysBetweenEntries
   */
  avgDailyWaterProduction: number;
  /** 
   * Number of days between this entry and the previous entry
   * 1 = consecutive days (daily check), 2 = every other day, etc.
   */
  daysBetweenEntries: number;
  
  // ==================== HAUL DATA ====================
  
  /** 
   * Oil hauled/sold for this date in BBL (REVENUE - you get paid)
   * Added to production calculation as oil that left the tank
   */
  oilHauledBBL: number;
  /** 
   * Water hauled/disposed for this date in BBL (EXPENSE - you pay ~$300/load)
   * NOT added to production - this is disposal, not production
   */
  waterHauledBBL: number;
  
  // ==================== PREVIOUS ENTRY REFERENCE ====================
  
  /** Previous entry's net oil (for audit trail) */
  previousNetOilBBL: number;
  /** Previous entry's gross water (for audit trail) */
  previousWaterBBL: number;
  /** Date of the previous entry (YYYY-MM-DD) */
  previousEntryDate: string | null;
  /** Whether previous entry data was available for calculations */
  hasPreviousEntryData: boolean;
  
  // ==================== LEGACY FIELDS (for backward compatibility) ====================
  
  /** @deprecated Use avgDailyOilProduction instead */
  dailyOilProduction: number;
  /** @deprecated Use avgDailyWaterProduction instead */
  dailyWaterProduction: number;
  /** @deprecated Use previousNetOilBBL instead */
  yesterdayNetOilBBL: number;
  /** @deprecated Use previousWaterBBL instead */
  yesterdayWaterBBL: number;
  /** @deprecated Use hasPreviousEntryData instead */
  hasYesterdayData: boolean;
  
  /** Timestamp when calculations were made */
  calculatedAt: string;
}

/**
 * Previous entry data for daily production calculation
 * Can be yesterday's entry OR the most recent entry if well isn't checked daily
 * 
 * @example Daily well (checked every day):
 *   previousEntry.date = "2025-12-09" (1 day ago)
 * 
 * @example Every-other-day well:
 *   previousEntry.date = "2025-12-08" (2 days ago)
 * 
 * @example Weekly well:
 *   previousEntry.date = "2025-12-03" (7 days ago)
 */
export interface PreviousEntryData {
  netOilBBL: number;
  grossWaterBBL: number;
  /** Date of the previous entry (YYYY-MM-DD format) */
  date: string;
}

/** @deprecated Use PreviousEntryData instead */
export type YesterdayEntryData = PreviousEntryData;

/**
 * Calculate entry totals at save time including daily production
 * 
 * PRODUCTION FORMULAS:
 * - totalOilProduction = (todayNetOil - previousNetOil) + oilHauled
 * - avgDailyOilProduction = totalOilProduction / daysBetweenEntries
 * 
 * IMPORTANT: Water hauling is NOT added to water production!
 * - Oil hauling = REVENUE (adds to production - the oil was sold)
 * - Water hauling = EXPENSE (disposal cost ~$300/load - NOT production)
 * 
 * This accounts for:
 * 1. Level change in tanks (can be negative if hauled)
 * 2. Oil hauled/sold (adds to oil production - the oil left the tank)
 * 3. Kolor-Kut water deduction (separates oil from water in oil tanks)
 * 4. Days between entries (for average daily production)
 * 
 * @param date - Entry date in YYYY-MM-DD format
 * @param oilTanks - Oil tank gauges from entry
 * @param waterTanks - Water tank gauges from entry
 * @param haulSummary - Total BBL hauled for oil and water
 * @param previousEntry - Previous entry's calculated values (null if first entry)
 * @param wellConfig - Well configuration for BBL/inch values
 * @returns Calculated totals to store with the entry
 */
export function calculateEntryTotals(
  date: string,
  oilTanks: StoredTankGauge[],
  waterTanks: StoredTankGauge[],
  haulSummary: HaulSummary = { oilBBL: 0, waterBBL: 0 },
  previousEntry: PreviousEntryData | null = null,
  wellConfig?: { oilTanks?: { tankId: string; bblPerInch?: number }[]; waterTanks?: { tankId: string; bblPerInch?: number }[] }
): EntryCalculatedTotals {
  // Validate inputs to prevent NaN
  if (!Array.isArray(oilTanks)) oilTanks = [];
  if (!Array.isArray(waterTanks)) waterTanks = [];
  if (!haulSummary) haulSummary = { oilBBL: 0, waterBBL: 0 };

  // Sum oil tank volumes
  let totalOilBBL = 0;
  let kolorKutWaterBBL = 0;
  
  for (const tank of oilTanks) {
    // Use stored barrels value (already calculated in DataEntry)
    const barrels = safeNumber(tank.barrels);
    totalOilBBL += barrels;
    
    // Calculate Kolor-Kut water if present
    if (tank.kolorKut && safeNumber(tank.kolorKut.waterCutInches) > 0) {
      // Find BBL/inch from well config or estimate from tank
      const configTank = wellConfig?.oilTanks?.find(t => t.tankId === tank.tankId);
      const bblPerInch = configTank?.bblPerInch || estimateBblPerInch(barrels, tank.topGauge);
      
      kolorKutWaterBBL += safeNumber(tank.kolorKut.waterCutInches) * bblPerInch;
    }
  }
  
  // Net oil = total - Kolor-Kut water
  const netOilBBL = Math.max(0, totalOilBBL - kolorKutWaterBBL);
  
  // Sum water tank volumes
  let grossWaterBBL = 0;
  for (const tank of waterTanks) {
    grossWaterBBL += safeNumber(tank.barrels);
  }
  
  // Add Kolor-Kut detected water to total water
  const totalWaterBBL = grossWaterBBL + kolorKutWaterBBL;
  
  // Water cut percentage
  const totalFluid = netOilBBL + totalWaterBBL;
  const waterCutPercent = totalFluid > 0 
    ? (totalWaterBBL / totalFluid) * 100 
    : 0;

  // =========================================================================
  // PRODUCTION CALCULATION
  // 
  // Key insight: Wells aren't always checked daily!
  // - Some wells checked daily (daysBetween = 1)
  // - Some wells checked every other day (daysBetween = 2)
  // - Some wells checked weekly (daysBetween = 7)
  // 
  // We calculate:
  // 1. TOTAL production since last entry
  // 2. AVERAGE daily production = total / days
  // 
  // IMPORTANT: Oil and Water hauling are DIFFERENT!
  // - Oil hauling = REVENUE (buyer pays you ~$70-80/bbl)
  //   → Oil hauled IS added to production (the oil was produced and sold)
  // - Water hauling = EXPENSE (you pay ~$300/load to dispose)
  //   → Water hauled is NOT added to production (it's just disposal)
  // =========================================================================
  
  const hasPreviousEntryData = previousEntry !== null;
  const previousNetOilBBL = previousEntry?.netOilBBL ?? 0;
  const previousWaterBBL = previousEntry?.grossWaterBBL ?? 0;
  const previousEntryDate = previousEntry?.date ?? null;
  
  let totalOilProduction = 0;
  let totalWaterProduced = 0;
  let avgDailyOilProduction = 0;
  let avgDailyWaterProduction = 0;
  let daysBetweenEntries = 1; // Default to 1 day
  
  if (hasPreviousEntryData && previousEntryDate) {
    // Calculate days between entries
    daysBetweenEntries = Math.max(1, daysBetweenIsoDates(previousEntryDate, date));
    
    // Total oil production = level change + oil hauled/sold
    // If tank went down 50 BBL but we sold 100 BBL, we produced 50 BBL net
    // If tank went up 20 BBL and we sold 0 BBL, we produced 20 BBL
    totalOilProduction = (netOilBBL - previousNetOilBBL) + safeNumber(haulSummary.oilBBL);
    
    // Total water produced = level change ONLY (water hauling is disposal, NOT production)
    // We track water hauled separately but don't add it to "water produced"
    totalWaterProduced = grossWaterBBL - previousWaterBBL;
    
    // Average daily production = total / days
    // This gives consistent metrics regardless of check frequency
    avgDailyOilProduction = totalOilProduction / daysBetweenEntries;
    avgDailyWaterProduction = totalWaterProduced / daysBetweenEntries;
    
    if (safeNumber(haulSummary.waterBBL) > 0) {
    }
    if (safeNumber(haulSummary.oilBBL) > 0) {
    }
  } else {
    // First entry - no previous data available
    // Set production to 0 (we can't calculate differential)
    // This is expected for new wells or first entry after gap
  }
  
  return {
    calculatedFor: date,
    totalOilBBL: round2(totalOilBBL),
    netOilBBL: round2(netOilBBL),
    kolorKutWaterBBL: round2(kolorKutWaterBBL),
    grossWaterBBL: round2(grossWaterBBL),
    waterCutPercent: round1(waterCutPercent),
    
    // New production fields
    totalOilProduction: round2(totalOilProduction),
    totalWaterProduced: round2(totalWaterProduced),
    avgDailyOilProduction: round2(avgDailyOilProduction),
    avgDailyWaterProduction: round2(avgDailyWaterProduction),
    daysBetweenEntries,
    
    // Haul data
    oilHauledBBL: round2(safeNumber(haulSummary.oilBBL)),
    waterHauledBBL: round2(safeNumber(haulSummary.waterBBL)),
    
    // Previous entry reference
    previousNetOilBBL: round2(previousNetOilBBL),
    previousWaterBBL: round2(previousWaterBBL),
    previousEntryDate,
    hasPreviousEntryData,
    
    // Legacy fields (for backward compatibility)
    dailyOilProduction: round2(avgDailyOilProduction), // Now returns average
    dailyWaterProduction: round2(avgDailyWaterProduction), // Now returns average  
    yesterdayNetOilBBL: round2(previousNetOilBBL),
    yesterdayWaterBBL: round2(previousWaterBBL),
    hasYesterdayData: hasPreviousEntryData,
    
    calculatedAt: new Date().toISOString(),
  };
}

/**
 * Safely convert a value to number, defaulting to 0 for invalid inputs
 * Prevents NaN propagation in calculations
 */
function safeNumber(value: unknown): number {
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
}

/**
 * Estimate BBL/inch from stored values
 * Used when well config doesn't have BBL/inch defined
 */
function estimateBblPerInch(barrels: number, topGaugeInches: number): number {
  const b = safeNumber(barrels);
  const t = safeNumber(topGaugeInches);
  if (t <= 0) return 2.5; // Default estimate for 400 BBL tank
  return b / t;
}

/**
 * Round to 2 decimal places
 */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Round to 1 decimal place
 */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

// ==================== EXPORT DEFAULT SERVICE ====================

export const ProductionCalculatorService = {
  // Tank calculations
  getBblPerInch,
  inchesToBarrels,
  calculateKolorKutAdjustment,
  
  // Production calculations
  calculateTankProduction,
  calculateDailyProduction,
  calculateEntryTotals, // Save-time calculation with yesterday comparison
  
  // Validation
  validateProductionEntry,
  validateEntryBeforeSave, // Pre-save validation with warnings
  
  // Historical analysis
  calculateAverages,
  calculateTotals,
  analyzeVariance,
  buildProductionComparison,
  
  // Batch operations
  calculateBatchProduction,
  
  // Utilities
  getPreviousDate,
  getDateRange,
  formatProductionDate,
  getTankFillPercent,
  getTankStatus,
  
  // Constants
  STANDARD_TANK_CONFIGS,
};

export default ProductionCalculatorService;
